import * as React from 'react';
import { Monitor, Moon, Sun } from 'lucide-react';
import { useTheme } from 'next-themes';

type ThemeValue = 'light' | 'dark' | 'system';

const OPTIONS: { value: ThemeValue; label: string; Icon: React.ComponentType<{ className?: string }> }[] = [
  { value: 'light', label: 'Light mode', Icon: Sun },
  { value: 'dark', label: 'Dark mode', Icon: Moon },
  { value: 'system', label: 'System theme', Icon: Monitor },
];

function useResolvedTheme(): { value: ThemeValue; setTheme: (v: ThemeValue) => void; mounted: boolean } {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => {
    setMounted(true);
  }, []);
  const value: ThemeValue = mounted ? (theme as ThemeValue) || 'system' : 'system';
  return { value, setTheme: setTheme as (v: ThemeValue) => void, mounted };
}

/**
 * Segmented theme toggle used on auth pages and in the sidebar when expanded.
 * Keeps the same three options (Light / Dark / System) everywhere so users
 * build muscle memory.
 */
export function ThemeToggle({ className = '' }: { className?: string }) {
  const { value, setTheme } = useResolvedTheme();

  return (
    <div
      className={`rounded-2xl border border-border/80 bg-card/95 p-1 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-card/80 ${className}`}
      role="group"
      aria-label="Theme"
    >
      <div className="flex items-center gap-1">
        {OPTIONS.map(({ value: opt, label, Icon }) => (
          <button
            key={opt}
            type="button"
            aria-label={label}
            aria-pressed={value === opt}
            onClick={() => setTheme(opt)}
            className={`flex-1 rounded-xl p-2 transition-colors ${
              value === opt ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
            }`}
          >
            <Icon className="mx-auto h-4 w-4" />
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Single-button toggle that cycles Light -> Dark -> System. Used in the
 * collapsed sidebar where there isn't room for a segmented control.
 */
export function ThemeToggleCompact({ className = '' }: { className?: string }) {
  const { value, setTheme } = useResolvedTheme();

  const idx = OPTIONS.findIndex((o) => o.value === value);
  const current = OPTIONS[idx === -1 ? 2 : idx];
  const next = OPTIONS[(idx + 1) % OPTIONS.length];
  const Icon = current.Icon;

  return (
    <button
      type="button"
      onClick={() => setTheme(next.value)}
      aria-label={`Switch to ${next.label.toLowerCase()}`}
      title={`Theme: ${current.label.replace(' mode', '').replace(' theme', '')} — click for ${next.label.toLowerCase()}`}
      className={`flex h-9 w-9 items-center justify-center rounded-xl border border-border/80 bg-card text-muted-foreground transition-colors hover:bg-muted hover:text-foreground ${className}`}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}
